import { useSyncExternalStore } from 'react';
import {
  createApiClient,
  createTokenSession,
  decodeJwt,
  decodeTokenScopes,
  webStore,
} from '@nyce/admin-kit/api';
import { expandScopes, hubApi, type LoginResponse, type Me } from './api';

export const BASE_URL: string = import.meta.env.VITE_API_BASE_URL || '/api/ophub';

// Own prefix: an Operations Hub tab never shares tokens with other nyce consoles on the same origin.
const PREFIX = 'oph';
const store = webStore(typeof sessionStorage !== 'undefined' ? sessionStorage : undefined);
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

export const session = createTokenSession({
  baseUrl: BASE_URL,
  store,
  prefix: PREFIX,
  onExpired: () => clearLogin(),
});
export const client = createApiClient({ baseUrl: BASE_URL, auth: session });
export const api = hubApi(client);

export interface HubLogin {
  username: string;
  /** The `sub` claim (AUTH user id/code). */
  sub: string;
  /** AUTH tenant id (`tid` claim, or `/me`). */
  tenantId?: string;
  /** Effective hub scopes (login/`/me` response, already expanded; the token's `scope` claim as fallback). */
  scopes: string[];
}

function readScopes(token: string): string[] {
  const raw = store.get(`${PREFIX}:scopes`);
  if (raw) {
    try {
      const list = JSON.parse(raw);
      if (Array.isArray(list)) return expandScopes(list.filter((s): s is string => typeof s === 'string'));
    } catch {
      /* fall through */
    }
  }
  return expandScopes(decodeTokenScopes(token));
}

function read(): HubLogin | null {
  const token = session.peek();
  if (!token) return null;
  const claims = decodeJwt(token) ?? {};
  return {
    username: store.get(`${PREFIX}:username`) ?? '',
    sub: store.get(`${PREFIX}:sub`) || (typeof claims.sub === 'string' ? claims.sub : ''),
    tenantId: store.get(`${PREFIX}:tenant`) || (typeof claims.tid === 'string' ? claims.tid : undefined),
    scopes: readScopes(token),
  };
}
let snapshot = read();

export function storeLogin(res: LoginResponse): void {
  session.setTokens(res);
  void import('./identity/session').then((m) => m.storeTargetLogins(res.targets));
  store.set(`${PREFIX}:username`, res.user?.name || res.user?.sub || '');
  store.set(`${PREFIX}:sub`, res.user?.sub ?? '');
  store.set(`${PREFIX}:scopes`, JSON.stringify(res.scopes ?? []));
  snapshot = read();
  notify();
}

/** Apply a fresh `/me` (scopes may have changed since sign-in). */
export function applyMe(me: Me): void {
  if (!session.peek()) return;
  store.set(`${PREFIX}:username`, me.name || me.sub);
  store.set(`${PREFIX}:sub`, me.sub);
  store.set(`${PREFIX}:tenant`, me.tenantId ?? '');
  store.set(`${PREFIX}:scopes`, JSON.stringify(me.scopes ?? []));
  const next = read();
  if (JSON.stringify(next) !== JSON.stringify(snapshot)) {
    snapshot = next;
    notify();
  }
}

export function clearLogin(): void {
  session.clear();
  // The environment sessions were this operator's: never inherited by the next sign-in in this tab.
  void import('./identity/session').then((m) => m.forgetTargetSessions());
  for (const k of ['username', 'sub', 'tenant', 'scopes']) store.remove(`${PREFIX}:${k}`);
  snapshot = read();
  notify();
}

export async function logout(): Promise<void> {
  try {
    await (await import('./identity/session')).signOutAllTargets();
  } catch {
    /* best effort */
  }
  try {
    await api.logout(session.refreshToken() ?? undefined);
  } catch {
    /* best effort */
  }
  clearLogin();
}

export function useHubLogin(): HubLogin | null {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => snapshot,
    () => snapshot
  );
}
